import { useEffect, useRef, type ReactNode } from 'react';
import type { Channel, SportEventView, VodItem, WatchProgress } from '@player/domain';
import { playableChannelOf, watchStateOf } from '@player/domain';
import type { AggregatorOnDemandRef, FocusPort } from '@player/ports';
import { FONT_STACK, colors, layout } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { useSpatialFocus } from '../useSpatialFocus.js';
import { PosterTile } from '../components/PosterTile.js';
import { channelColorOf } from '../channelColors.js';

/**
 * La pagina di un aggregatore (03-SCHERMATE-TV §3.2): «Calcio», «Cinema», «Documentari».
 *
 * ## Esiste perché una piastrella deve portare da qualche parte
 *
 * La fascia «Esplora» si disegnava e premerla non faceva niente. Una card che non porta a un
 * contenuto è un tasto rotto: chi guarda ci prova, non succede nulla, e conclude che l'app è rotta —
 * non che quella funzione non c'è. Il difetto peggiore di una pagina mancante è la card che finge
 * che ci sia.
 *
 * ## La regola la valuta il server, non l'app
 *
 * L'app non riceve la regola dell'aggregatore e non la applica: chiede
 * `GET /api/v1/app/aggregators/:id` e riceve **tre elenchi già decisi** — eventi, canali, on demand.
 * Se la valutasse l'app, cambiare un aggregatore richiederebbe di ripubblicarla sullo store, che è
 * esattamente ciò che questa architettura evita.
 *
 * Quel che resta all'app è **lo stato mostrato**: «in diretta adesso» dipende dall'istante in cui si
 * guarda lo schermo, non da quello in cui il server ha risposto.
 */

export interface AggregatorScreenProps {
  readonly focusPort: FocusPort;
  readonly title: string;
  readonly color: string;
  readonly loading: boolean;
  /** Il codice dell'errore, quando la chiamata non è riuscita. */
  readonly errorCode: string | null;
  /** Gli eventi già divisi dal dominio: i live davanti, i finiti fuori. */
  readonly events: ReadonlyArray<SportEventView>;
  /** I canali della regola che questa linea vede, già risolti sul listino. */
  readonly channels: ReadonlyArray<Channel>;
  readonly onDemand: ReadonlyArray<AggregatorOnDemandRef>;
  /** Il catalogo locale, per disegnare una locandina con genere e avanzamento quando c'è. */
  readonly vod: ReadonlyArray<VodItem>;
  readonly progress: WatchProgress;
  readonly onOpenChannel: (channelId: string) => void;
  readonly onOpenVod: (ref: string) => void;
  readonly onExit: () => void;
}

export function AggregatorScreen({
  focusPort,
  title,
  color,
  loading,
  errorCode,
  events,
  channels,
  onDemand,
  vod,
  progress,
  onOpenChannel,
  onOpenVod,
  onExit,
}: AggregatorScreenProps): ReactNode {
  const translator = useTranslator();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const handlers = useRef({ onOpenChannel, onOpenVod, onExit });
  useEffect(() => {
    handlers.current = { onOpenChannel, onOpenVod, onExit };
  }, [onOpenChannel, onOpenVod, onExit]);

  useSpatialFocus(focusPort, frameRef, {
    onBack: () => {
      handlers.current.onExit();
      return true;
    },
  });

  const byId = new Map(vod.map((item) => [item.id, item]));
  const empty = events.length === 0 && channels.length === 0 && onDemand.length === 0;

  return (
    <div
      ref={frameRef}
      data-aggregator="1"
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '56px 0 80px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 30,
      }}
    >
      <div style={{ padding: '0 72px', display: 'flex', alignItems: 'center', gap: 18 }}>
        {/* La barra di colore dell'aggregatore, quella scelta nel pannello. */}
        <span style={{ width: 10, height: 56, borderRadius: 5, background: color }} />
        <h1 style={{ font: `800 60px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>{title}</h1>
      </div>

      {errorCode !== null ? (
        /*
          **Un vicolo cieco deve avere una porta.**

          Qui c'era il solo messaggio, e nient'altro: nessun elemento a fuoco in tutta la schermata.
          L'unica uscita era il tasto Indietro, e su un televisore quello è un filo solo — se la
          piattaforma non lo consegna, o se il fuoco è finito in un posto che non lo ascolta, da lì
          non si esce più. Riportato dal campo così: «va in errore e la home si blocca su
          quell'informazione».

          Il tasto non è una comodità: è l'unica cosa che rende questa schermata una schermata invece
          di un muro. Vale anche per lo stato vuoto qui sotto, per la stessa ragione.
        */
        <div style={{ padding: '30px 72px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 22 }}>
          <p data-aggregator-error="1" style={{ margin: 0, font: `600 28px ${FONT_STACK}`, color: colors.errorText }}>
            {translator.t(`errors.${errorCode}`)}
          </p>
          <BackButton label={translator.t('aggregator.back')} onSelect={() => handlers.current.onExit()} />
        </div>
      ) : loading ? (
        <p data-aggregator-loading="1" style={{ padding: '30px 72px', font: `600 28px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('aggregator.loading')}
        </p>
      ) : empty ? (
        /*
          Vuoto **adesso**, e va detto così: un aggregatore «Calcio» senza partite in corso non è un
          aggregatore rotto, è lunedì mattina. Un elenco vuoto senza una parola si legge come un
          guasto.
        */
        <div style={{ padding: '30px 72px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 22 }}>
          <p data-aggregator-empty="1" style={{ margin: 0, font: `600 28px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t('aggregator.empty')}
          </p>
          <BackButton label={translator.t('aggregator.back')} onSelect={() => handlers.current.onExit()} />
        </div>
      ) : (
        <>
          {events.length > 0 && (
            <Section title={translator.t('aggregator.section.events')} count={events.length}>
              {events.map((view) => {
                /*
                  Il canale **che questa linea possiede**, non il primo dell'elenco.

                  `channelIds` porta i canali di tutta l'installazione — la chiave in Redis è una
                  sola — quindi `[0]` poteva essere il canale di un altro pacchetto: la card restava
                  premibile e non apriva niente. Vedi `playableChannelOf`.
                */
                const channelId = playableChannelOf(view.event, channels)?.id;
                const live = view.state === 'live';
                const time = new Date(view.event.startMs).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                return (
                  <button
                    key={view.event.id}
                    type="button"
                    data-f="1"
                    data-aggregator-event={view.event.id}
                    // Senza un canale non c'è niente da aprire: si mostra spento invece di fingere.
                    disabled={channelId === undefined}
                    onClick={() => {
                      if (channelId !== undefined) handlers.current.onOpenChannel(channelId);
                    }}
                    style={{ ...cardStyle, width: 380, opacity: channelId === undefined ? 0.55 : 1 }}
                  >
                    <span
                      style={{
                        alignSelf: 'flex-start',
                        padding: '6px 14px',
                        borderRadius: 8,
                        font: `800 18px ${FONT_STACK}`,
                        letterSpacing: '.04em',
                        background: live ? colors.live : colors.info,
                        color: live ? colors.background : colors.text,
                      }}
                    >
                      {live ? translator.t('search.live') : time}
                    </span>
                    <span style={{ font: `700 26px/1.2 ${FONT_STACK}`, wordBreak: 'break-word' }}>
                      {view.event.title}
                    </span>
                    {view.event.competition !== '' && (
                      <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
                        {view.event.competition}
                      </span>
                    )}
                  </button>
                );
              })}
            </Section>
          )}

          {channels.length > 0 && (
            <Section title={translator.t('aggregator.section.channels')} count={channels.length}>
              {channels.map((channel) => {
                const tone = channelColorOf(channel.name);
                return (
                  <button
                    key={channel.id}
                    type="button"
                    data-f="1"
                    data-aggregator-channel={channel.id}
                    onClick={() => handlers.current.onOpenChannel(channel.id)}
                    style={{ ...cardStyle, width: 300, minHeight: 110, flexDirection: 'row', alignItems: 'center', gap: 14 }}
                  >
                    <span
                      style={{
                        flex: 'none',
                        width: 62,
                        height: 62,
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: tone.background,
                        color: tone.foreground,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        font: `800 20px ${FONT_STACK}`,
                      }}
                    >
                      {channel.logoUrl === null ? (
                        channel.number
                      ) : (
                        <img src={channel.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      )}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span
                        style={{
                          font: `700 24px ${FONT_STACK}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {channel.name}
                      </span>
                      <span style={{ font: `500 19px ${FONT_STACK}`, color: colors.textDim }}>{channel.number}</span>
                    </span>
                  </button>
                );
              })}
            </Section>
          )}

          {onDemand.length > 0 && (
            <Section title={translator.t('aggregator.section.onDemand')} count={onDemand.length} grid>
              {onDemand.map((entry) => {
                /*
                  Il titolo e la locandina arrivano **dal server**: la fascia si disegna anche prima
                  che il catalogo on demand sia in memoria. Genere e avanzamento si aggiungono se il
                  titolo è già in catalogo, e non si aspetta che lo sia.
                */
                const item = byId.get(entry.ref);
                const state = item === undefined ? null : watchStateOf(item, progress);
                return (
                  <PosterTile
                    key={entry.ref}
                    title={entry.title}
                    subtitle={
                      item === undefined
                        ? translator.t(entry.kind === 'series' ? 'vod.kind.series' : 'vod.kind.movie')
                        : [
                            translator.t(item.kind === 'series' ? 'vod.kind.series' : 'vod.kind.movie'),
                            item.genre,
                            item.year > 0 ? String(item.year) : '',
                          ]
                            .filter((part) => part !== '')
                            .join(' · ')
                    }
                    posterUrl={entry.image}
                    percent={state !== null && state.percent > 0 ? state.percent : null}
                    watched={state?.watched ?? false}
                    lockedRating={null}
                    focused={false}
                    onSelect={() => handlers.current.onOpenVod(entry.ref)}
                  />
                );
              })}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * La porta di una schermata che altrimenti non ne avrebbe.
 *
 * `data-f` perché il fuoco spaziale lo trovi, e `autoFocus` perché ce lo porti da solo: un tasto che
 * si vede e che bisogna cercare col telecomando è mezzo tasto.
 */
function BackButton({ label, onSelect }: { label: string; onSelect: () => void }): ReactNode {
  return (
    <button
      type="button"
      data-f="1"
      data-aggregator-back="1"
      autoFocus
      onClick={onSelect}
      style={{
        height: 64,
        padding: '0 30px',
        border: 0,
        borderRadius: 14,
        cursor: 'pointer',
        background: colors.surfaceRaised,
        color: colors.text,
        font: `600 25px ${FONT_STACK}`,
      }}
    >
      {label}
    </button>
  );
}

function Section({
  title,
  count,
  grid = false,
  children,
}: {
  title: string;
  count: number;
  /**
   * Griglia a scorrimento verticale invece di una riga che scorre a destra.
   *
   * Serve alla sezione on demand, che è l'unica delle tre che può contenere **tutto un catalogo**:
   * eventi e canali di un aggregatore sono una manciata, i film no. Una riga orizzontale su
   * duecento titoli è un corridoio — per arrivare al centesimo si preme ottanta volte, e non si ha
   * mai idea di quanti siano.
   */
  grid?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <section data-aggregator-section={title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ padding: '0 72px', display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <span style={{ font: `700 32px ${FONT_STACK}` }}>{title}</span>
        {/* Il conto accanto al nome: dice se vale la pena scorrere prima di scorrere. */}
        <span style={{ font: `600 22px ${FONT_STACK}`, color: colors.textDim }}>{count}</span>
      </div>
      <div
        data-aggregator-grid={grid ? '1' : '0'}
        style={
          grid
            ? {
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, ${layout.posterTileWidth}px)`,
                justifyContent: 'space-between',
                gap: '28px 16px',
                padding: '8px 72px 12px',
              }
            : { display: 'flex', gap: 16, overflowX: 'auto', scrollbarWidth: 'none', padding: '8px 72px 12px' }
        }
      >
        {children}
      </div>
    </section>
  );
}

const cardStyle = {
  flex: 'none',
  minHeight: 170,
  padding: 20,
  borderRadius: 16,
  border: 0,
  background: colors.surface,
  color: colors.text,
  textAlign: 'left' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 10,
  cursor: 'pointer',
};
